import type { EvidenceRecord } from "@ti/core";
import { Camera, FileCheck2, FileCode2, Gavel, Package, ShieldCheck, Workflow } from "lucide-react";
import type { ReactNode } from "react";

export type EvidenceKind = EvidenceRecord["kind"];

export const EVIDENCE_KINDS: EvidenceKind[] = ["control_outcome", "issue_transition", "endorsement", "score_snapshot", "policy_version", "claim", "trace_bundle"];

export const EVIDENCE_KIND_META: Record<EvidenceKind, { label: string; icon: ReactNode }> = {
  control_outcome: { label: "Control outcome", icon: <ShieldCheck /> },
  issue_transition: { label: "Issue transition", icon: <Workflow /> },
  endorsement: { label: "Endorsement", icon: <FileCheck2 /> },
  score_snapshot: { label: "Score snapshot", icon: <Camera /> },
  policy_version: { label: "Policy version", icon: <FileCode2 /> },
  claim: { label: "Claim", icon: <Gavel /> },
  trace_bundle: { label: "Trace bundle", icon: <Package /> },
};

export interface ReadinessFramework {
  id: string;
  name: string;
  clause: string;
  evidence: string;
  /** Ledger record kinds that evidence this clause; any one present counts. */
  kinds: EvidenceKind[];
}

/** Frameworks the evidence ledger maps to in the readiness report. */
export const READINESS_FRAMEWORKS: ReadinessFramework[] = [
  { id: "eu-art12", name: "EU AI Act", clause: "Art. 12 · Record-keeping", evidence: "Automatic, tamper-evident event logs over the system lifetime", kinds: ["control_outcome", "issue_transition", "score_snapshot"] },
  { id: "eu-art72", name: "EU AI Act", clause: "Art. 72 · Post-market monitoring", evidence: "Continuous signals, scores and issue lifecycle per agent version", kinds: ["score_snapshot", "issue_transition"] },
  { id: "eu-art73", name: "EU AI Act", clause: "Art. 73 · Serious incident reporting", evidence: "Incident timeline, linked traces and claim evidence packs", kinds: ["issue_transition", "claim", "trace_bundle"] },
  { id: "nist", name: "NIST AI RMF 1.0", clause: "Measure · Manage", evidence: "Risk measurement, platform-reported controls and remediation verification", kinds: ["score_snapshot", "control_outcome", "issue_transition"] },
  { id: "iso42001", name: "ISO/IEC 42001", clause: "Clauses 8–10 · Operation & improvement", evidence: "Operational controls, monitoring records and corrective actions", kinds: ["control_outcome", "issue_transition", "endorsement"] },
  { id: "aiuc1", name: "AIUC-1", clause: "Agent insurability controls", evidence: "Authority profile and platform-reported control outcomes", kinds: ["control_outcome", "policy_version"] },
  { id: "owasp", name: "OWASP Agentic Top 10", clause: "Threat coverage", evidence: "Injection, excessive agency and tool-misuse signals with mitigations", kinds: ["control_outcome", "issue_transition"] },
];
